import { RiskFactors, RiskFactorsPerson } from '@lbcamden/shared-utils';
import { z } from 'zod';

import type {
  contactTypeSchema,
  createHouseholdMemberSchema,
  endHouseholdMemberSchema,
  householdMemberUpdateSchema,
  newPersonSchema,
  pcfraPersonSchema,
  personContactDetailsUpdateSchema,
  personSchema,
  personSearchInputSchema,
  tenantUpdateSchema,
  updateRpeepStatusSchema,
  updatePcfraReviewDateSchema,
  updatePcfraSelfReferredSchema,
  updatePcfraStatusSchema,
} from '../schemas/people';
import type { BaseResident } from './residentsReport';

import { TableRow, UserStoreTables } from './tinybase';

export type Person = z.infer<typeof personSchema>;

export type PcfraPerson = z.infer<typeof pcfraPersonSchema>;

export type PersonNameFields = Pick<Person, 'title' | 'forename' | 'surname'>;

export type WithoutAddedFields<TPerson extends Person | PcfraPerson | BaseResident> = Omit<
  TPerson,
  'fullName'
>;

export type TenantUpdate = z.infer<typeof tenantUpdateSchema>;

export type HouseholdMemberUpdate = z.infer<typeof householdMemberUpdateSchema>;

export type EndHouseholdMemberPayload = z.infer<typeof endHouseholdMemberSchema>;

export type PersonUpdate = TenantUpdate | HouseholdMemberUpdate;

export type ContactType = z.infer<typeof contactTypeSchema>;

export type ContactDetailsItem<TType extends ContactType = ContactType> = TableRow<
  UserStoreTables,
  'contactDetails'
> & {
  type: TType;
};

export type PersonContactDetails = {
  emailAddresses: ContactDetailsItem<'EMAIL'>[];
  mobileNumbers: ContactDetailsItem<'MOB'>[];
  smsOnlyNumbers: ContactDetailsItem<'SMSTEXTNO'>[];
  otherPhoneNumbers: ContactDetailsItem<'HOMETEL' | 'TELEPHONE'>[];
};

export type ContactDetailsArrayKey = keyof PersonContactDetails;

export type PersonContactDetailsUpdate = z.infer<typeof personContactDetailsUpdateSchema>;

export type PersonWithContactDetails = Person & PersonContactDetails;

export type RiskFactorKey = keyof RiskFactors;

export type PersonRiskFactorItem = Override<
  TableRow<UserStoreTables, 'peopleRiskFactors'>,
  { key: RiskFactorKey }
>;

export type PersonRiskFactors = Pick<RiskFactorsPerson, 'riskFactors' | 'supportNeedRiskFactors'>;

export type PersonAttributeItem = Override<
  TableRow<UserStoreTables, 'peopleAttributes'>,
  { categoryCode: PersonAttributeCategoryCode }
>;

export type PersonAttributes = {
  supportNeeds: PersonAttributeItem[];
  reasonableAdjustments: PersonAttributeItem[];
  communicationAdjustments: PersonAttributeItem[];
  communicationPreferences: PersonAttributeItem[];
};

export type PersonWithAttributes = Person & PersonAttributes;

/** Ensure values align with the relevant NEC value (PEOPLE_ATTRIBUTES.PEAT_HRV_PATC_CODE) */
export enum PersonAttributeCategoryCode {
  SupportNeed = 'SN_NEED',
  SupportNeedRiskFactor = 'SN_PCFRA',
  ReasonableAdjustment = 'SN_ADJUST',
  CommunicationAdjustment = 'SN_COMMADJ',
  CommunicationPreference = 'SN_COMMPRE',
}

export type FullPerson = Person & PersonContactDetails & PersonAttributes;

export type FullPcfraPerson = PcfraPerson & PersonContactDetails & PersonRiskFactors;

export type NewPersonPayload = z.infer<typeof newPersonSchema>;

export type CreateHouseholdMemberPayload = z.infer<typeof createHouseholdMemberSchema>;

export type PersonSearchInput = z.infer<typeof personSearchInputSchema>;

export type PersonSearchResult = Pick<
  Person,
  | 'personId'
  | 'title'
  | 'surname'
  | 'forename'
  | 'otherName'
  | 'dateOfBirth'
  | 'isHistoric'
  | 'isTenant'
> &
  Partial<Pick<Person, 'householdPersonId' | 'householdId' | 'propertyId' | 'tenancyId'>>;

export type PersonFormValuesResult = {
  personId: number;
} & Partial<Omit<FullPerson, 'personId'>>;

// Ensure values are kept in line with NEC (FSC.FIRST_REF_VALUES WHERE FRV_FRD_DOMAIN = 'PCFRA_STATUS')
export enum PcfraStatus {
  None = '',
  Requested = 'REQUESTED',
  Unnecessary = 'UNNECESS',
  Necessary = 'NECESSARY',
  Drafting = 'DRAFTING',
  Submitted = 'SUBMITTED',
  Active = 'ACTIVE',
  ReviewNeeded = 'NEEDREVIEW',
}

// Ensure values are kept in line with NEC (FSC.FIRST_REF_VALUES WHERE FRV_FRD_DOMAIN = 'PCFRA_PEEP_STATUS')
export enum RpeepStatus {
  None = '',
  Active = 'ACTIVE',
  Revoked = 'REVOKED',
}

// Ensure values are kept in line with NEC (FSC.FIRST_REF_VALUES WHERE FRV_FRD_DOMAIN = 'SIB_SHEET_COLOURS')
export enum SibSheetColour {
  None = 'NONE',
  Amber = 'AMBER',
  Red = 'RED',
}

export type UpdatePcfraStatusPayload = z.infer<typeof updatePcfraStatusSchema>;

export type UpdateRpeepStatusPayload = z.infer<typeof updateRpeepStatusSchema>;

export type UpdatePcfraReviewDatePayload = z.infer<typeof updatePcfraReviewDateSchema>;

export type UpdatePcfraSelfReferredPayload = z.infer<typeof updatePcfraSelfReferredSchema>;
